/**
 * SportsCheckinPage — Customer-facing check-in page.
 * URL: /sports/checkin/:bookingId
 * Shows booking QR code + details. Customer shows this to staff at court.
 * No auth required.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { fmt12 } from '../utils/sportsUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDate(ds) {
  const d = new Date(ds + 'T00:00:00');
  return `${DOW[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function TimeRemaining({ minutes, started, ended }) {
  if (ended)    return <span style={{ color: '#FF2D55', fontWeight: 700 }}>Session Ended</span>;
  if (!started) return <span style={{ color: '#FFB300', fontWeight: 700 }}>{minutes}m until start</span>;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const label = h > 0 ? `${h}h ${m}m remaining` : `${m}m remaining`;
  return <span style={{ color: '#00E676', fontWeight: 700 }}>{label}</span>;
}

export default function SportsCheckinPage() {
  const { bookingId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const checkinUrl = `${window.location.origin}/sports/checkin/${bookingId}`;

  useEffect(() => {
    axios.get(`${BACKEND}/api/sports/public/checkin/${bookingId}`)
      .then(r => setData(r.data))
      .catch(() => setError('Could not load booking details'))
      .finally(() => setLoading(false));
  }, [bookingId, tick]);

  // Refresh every 60 seconds to update time remaining
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <div className="animate-spin h-8 w-8 rounded-full" style={{ border: '2px solid #E63946', borderTopColor: 'transparent' }} />
    </div>
  );

  if (error || !data) return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center', fontFamily: 'Manrope, sans-serif', color: '#fff' }}>
      <p style={{ fontSize: 48, marginBottom: 16 }}>❌</p>
      <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Booking Not Found</p>
      <p style={{ color: '#737373', marginBottom: 24 }}>{error || 'This booking could not be verified.'}</p>
      <Link to="/" style={{ color: '#E63946', fontSize: 14 }}>← Back to Home</Link>
    </div>
  );

  if (!data.valid) return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center', fontFamily: 'Manrope, sans-serif', color: '#fff' }}>
      <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FF2D5520', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <span style={{ fontSize: 36 }}>⚠️</span>
      </div>
      <p style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Booking Invalid</p>
      <p style={{ color: '#737373', marginBottom: 8 }}>{data.reason}</p>
      {data.customer_name && <p style={{ color: '#A3A3A3', fontSize: 14 }}>Customer: {data.customer_name}</p>}
      {data.status && <p style={{ color: '#FF2D55', fontSize: 13, marginTop: 8, textTransform: 'capitalize' }}>Status: {data.status}</p>}
    </div>
  );

  const statusColor = data.session_ended ? '#FF2D55' : data.session_started ? '#00E676' : '#FFB300';
  const statusBg   = data.session_ended ? '#FF2D5512' : data.session_started ? '#00E67612' : '#FFB30012';

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif', padding: '32px 20px', maxWidth: 440, margin: '0 auto' }}>

      {/* Status banner */}
      <div style={{ borderRadius: 12, padding: '12px 20px', background: statusBg, border: `1px solid ${statusColor}30`, textAlign: 'center', marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: statusColor, textTransform: 'uppercase', letterSpacing: 1 }}>
          {data.session_ended ? 'Session Ended' : data.session_started ? 'Session Active' : 'Upcoming Booking'}
        </p>
        <p style={{ fontSize: 22, fontWeight: 900, fontFamily: 'Oswald, sans-serif', color: statusColor, marginTop: 4 }}>
          <TimeRemaining minutes={data.session_ended ? 0 : data.session_started ? data.minutes_remaining : data.minutes_until} started={data.session_started} ended={data.session_ended} />
        </p>
      </div>

      {/* BRN — prominent reference number */}
      {data.ref_number && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: '#444', marginBottom: 4 }}>Booking Reference</p>
          <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 900, color: statusColor, letterSpacing: 3 }}>{data.ref_number}</p>
        </div>
      )}

      {/* QR Code — level H for best scanability */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#737373', marginBottom: 12 }}>Booking QR Code</p>
        <div style={{ background: '#fff', borderRadius: 16, padding: 16, display: 'inline-block', boxShadow: '0 0 40px rgba(230,57,70,0.2)' }}>
          <QRCodeSVG value={checkinUrl} size={200} bgColor="#ffffff" fgColor="#0A0A0A" level="H" includeMargin={true} />
        </div>
        <p style={{ fontSize: 11, color: '#555', marginTop: 10 }}>Show this to court staff when arriving</p>
      </div>

      {/* Booking details */}
      <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 16, padding: 20, marginBottom: 20 }}>
        <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 900, textTransform: 'uppercase', color: '#fff', marginBottom: 4 }}>
          {data.customer_name}
        </p>
        {data.customer_phone && (
          <p style={{ fontSize: 13, color: '#737373', marginBottom: 16 }}>{data.customer_phone}</p>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            ['Court', data.court_name],
            ['Sport', data.sport?.charAt(0).toUpperCase() + data.sport?.slice(1)],
            ['Date', fmtDate(data.date)],
            ['Time', `${fmt12(data.start_time)} – ${fmt12(data.end_time)}`],
            ['Amount', `₱${(data.amount || 0).toLocaleString()}`],
            data.amount_paid != null && data.balance_due > 0 && ['Paid', `₱${(data.amount_paid || 0).toLocaleString()}`],
            data.balance_due > 0 && ['Balance Due', `₱${(data.balance_due || 0).toLocaleString()}`],
            data.booking_type === 'competition' && ['Event', data.event_name],
          ].filter(Boolean).map(([k, v]) => v && (
            <div key={k} style={{ background: '#0A0A0A', borderRadius: 8, padding: '10px 12px' }}>
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#555', marginBottom: 3 }}>{k}</p>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Reference number + booking ID */}
      <p style={{ textAlign: 'center', fontSize: 11, color: '#2A2A2A', fontFamily: 'monospace' }}>
        {data.ref_number ? `${data.ref_number} · ` : ''}ID: {bookingId}
      </p>
    </div>
  );
}
